/**
 * Eight inline SVG piece sets, adapted from the Claude Design exploration at
 * "D:\NEXT Project\Chess, Lands and Pieces\Claude Design\src\pieces.jsx".
 *
 * Each renderer takes (type, isWhite) and pulls fill/stroke from the active
 * theme's CSS variables (--c-piece-light, --c-piece-dark, --c-piece-light-stroke,
 * --c-piece-dark-stroke), so the same renderer works across every theme.
 */
import type { PieceType, Color } from '@ccc/shared-types';

export type InlinePieceSetId =
  | 'heraldic'
  | 'geometric'
  | 'ornate'
  | 'petal'
  | 'botanical'
  | 'block'
  | 'neon_wire'
  | 'woodcut';

interface PaletteVars {
  fill: string;
  stroke: string;
  cutBg: string;
}

function paletteFor(color: Color): PaletteVars {
  if (color === 'white') {
    return {
      fill: 'rgb(var(--c-piece-light))',
      stroke: 'rgb(var(--c-piece-light-stroke))',
      cutBg: 'rgb(var(--c-bg))',
    };
  }
  return {
    fill: 'rgb(var(--c-piece-dark))',
    stroke: 'rgb(var(--c-piece-dark-stroke))',
    cutBg: 'rgb(var(--c-piece-light))',
  };
}

function letterFor(type: PieceType): 'P' | 'N' | 'B' | 'R' | 'Q' | 'K' {
  switch (type) {
    case 'pawn':
      return 'P';
    case 'knight':
      return 'N';
    case 'bishop':
      return 'B';
    case 'rook':
      return 'R';
    case 'mini-rook':
      return 'R';
    case 'queen':
      return 'Q';
    case 'king':
      return 'K';
  }
}

// === 1. HERALDIC ============================================================
function Heraldic({ type, isWhite }: { type: PieceType; isWhite: boolean }) {
  const { fill, stroke } = paletteFor(isWhite ? 'white' : 'black');
  const p = { fill, stroke, strokeWidth: 2.4, strokeLinejoin: 'round' as const, strokeLinecap: 'round' as const };
  const accent = { stroke, strokeWidth: 2.2, fill: 'none', strokeLinecap: 'round' as const };
  const t = letterFor(type);
  const base = <path d="M22 88 L28 72 L72 72 L78 88 Z" {...p} />;
  return (
    <svg viewBox="0 0 100 100" width="100%" height="100%" style={{ display: 'block' }}>
      {t === 'P' && (
        <>
          <circle cx="50" cy="30" r="10" {...p} />
          <path d="M41 39 Q50 43 59 39 L62 52 L38 52 Z" {...p} />
          <path d="M32 62 L38 52 L62 52 L68 62 L68 72 L32 72 Z" {...p} />
          {base}
        </>
      )}
      {t === 'N' && (
        <>
          <path
            d="M28 88 L30 60 Q24 50 30 38 L44 26 Q46 18 52 18 Q60 18 64 26 Q74 32 76 50 L70 56 L62 50 L57 56 L60 88 Z"
            {...p}
          />
          <circle cx="60" cy="34" r="2" fill={stroke} stroke="none" />
          <path d="M40 36 L46 32" {...accent} />
        </>
      )}
      {t === 'B' && (
        <>
          <circle cx="50" cy="10" r="4" {...p} />
          <path d="M50 14 Q62 30 60 52 L40 52 Q38 30 50 14 Z" {...p} />
          <path d="M50 22 L50 44" {...accent} />
          <path d="M36 52 L64 52 L66 62 L34 62 Z" {...p} />
          <path d="M30 72 L34 62 L66 62 L70 72 Z" {...p} />
          {base}
        </>
      )}
      {t === 'R' && (
        <>
          <path
            d="M25 28 L25 18 L34 18 L34 24 L46 24 L46 18 L54 18 L54 24 L66 24 L66 18 L75 18 L75 28 L70 28 L70 56 L75 56 L75 66 L25 66 L25 56 L30 56 L30 28 Z"
            {...p}
          />
          <line x1="30" y1="38" x2="70" y2="38" {...accent} />
          <line x1="30" y1="50" x2="70" y2="50" {...accent} />
          <path d="M22 88 L28 66 L72 66 L78 88 Z" {...p} />
        </>
      )}
      {t === 'Q' && (
        <>
          <circle cx="22" cy="20" r="3.5" {...p} />
          <circle cx="36" cy="14" r="3.5" {...p} />
          <circle cx="50" cy="10" r="3.5" {...p} />
          <circle cx="64" cy="14" r="3.5" {...p} />
          <circle cx="78" cy="20" r="3.5" {...p} />
          <path d="M20 24 L28 50 L40 22 L50 52 L60 22 L72 50 L80 24 L74 64 L26 64 Z" {...p} />
          {base}
        </>
      )}
      {t === 'K' && (
        <>
          <rect x="46" y="6" width="8" height="20" {...p} />
          <rect x="38" y="12" width="24" height="8" {...p} />
          <path d="M30 32 L40 26 L50 36 L60 26 L70 32 L72 56 L28 56 Z" {...p} />
          <path d="M28 56 L72 56 L70 66 L30 66 Z" {...p} />
          {base}
        </>
      )}
    </svg>
  );
}

// === 2. GEOMETRIC ===========================================================
function Geometric({ type, isWhite }: { type: PieceType; isWhite: boolean }) {
  const { fill, stroke } = paletteFor(isWhite ? 'white' : 'black');
  const p = { fill, stroke, strokeWidth: 3.5, strokeLinejoin: 'round' as const, strokeLinecap: 'round' as const };
  const t = letterFor(type);
  return (
    <svg viewBox="0 0 100 100" width="100%" height="100%" style={{ display: 'block' }}>
      {t === 'P' && (
        <>
          <circle cx="50" cy="38" r="14" {...p} />
          <path d="M30 78 Q50 60 70 78 Z" {...p} />
        </>
      )}
      {t === 'N' && (
        <>
          <polygon points="32,80 32,52 50,52 50,28 70,28 70,52 70,80" {...p} />
          <circle cx="62" cy="38" r="3" fill={stroke} stroke="none" />
        </>
      )}
      {t === 'B' && (
        <>
          <path d="M50 14 L68 56 Q60 80 50 80 Q40 80 32 56 Z" {...p} />
          <line x1="50" y1="28" x2="50" y2="46" stroke={stroke} strokeWidth="3" strokeLinecap="round" />
        </>
      )}
      {t === 'R' && (
        <>
          <path d="M26 36 L26 22 L36 22 L36 28 L46 28 L46 22 L54 22 L54 28 L64 28 L64 22 L74 22 L74 36 Z" {...p} />
          <rect x="30" y="36" width="40" height="38" {...p} />
          <path d="M22 80 L28 72 L72 72 L78 80 Z" {...p} />
        </>
      )}
      {t === 'Q' && (
        <>
          <circle cx="28" cy="22" r="4" {...p} />
          <circle cx="50" cy="14" r="4" {...p} />
          <circle cx="72" cy="22" r="4" {...p} />
          <path d="M26 28 L36 56 L50 30 L64 56 L74 28 L72 66 L28 66 Z" {...p} />
          <path d="M22 80 L28 66 L72 66 L78 80 Z" {...p} />
        </>
      )}
      {t === 'K' && (
        <>
          <rect x="46" y="14" width="8" height="20" {...p} />
          <rect x="38" y="20" width="24" height="8" {...p} />
          <circle cx="50" cy="52" r="16" {...p} />
          <path d="M22 80 L28 68 L72 68 L78 80 Z" {...p} />
        </>
      )}
    </svg>
  );
}

// === 3. ORNATE ==============================================================
function Ornate({ type, isWhite }: { type: PieceType; isWhite: boolean }) {
  const { fill, stroke } = paletteFor(isWhite ? 'white' : 'black');
  const p = { fill, stroke, strokeWidth: 2, strokeLinejoin: 'round' as const, strokeLinecap: 'round' as const };
  const swirl = { stroke, strokeWidth: 1.8, fill: 'none', strokeLinecap: 'round' as const };
  const t = letterFor(type);
  const body = (
    <>
      <path d="M42 78 Q38 70 40 60 L40 32 Q38 28 40 24 L60 24 Q62 28 60 32 L60 60 Q62 70 58 78 Z" {...p} />
      <ellipse cx="50" cy="82" rx="20" ry="3" {...p} />
      <circle cx="32" cy="45" r="1.5" fill={stroke} stroke="none" />
      <circle cx="68" cy="45" r="1.5" fill={stroke} stroke="none" />
      <path d="M40 36 Q36 38 36 42 M60 36 Q64 38 64 42" {...swirl} />
    </>
  );
  return (
    <svg viewBox="0 0 100 100" width="100%" height="100%" style={{ display: 'block' }}>
      {body}
      {t === 'P' && <circle cx="50" cy="14" r="6" {...p} />}
      {t === 'N' && (
        <>
          <path d="M38 20 Q44 6 50 12 Q56 6 62 20" {...p} />
          <circle cx="58" cy="14" r="1.5" fill={stroke} stroke="none" />
        </>
      )}
      {t === 'B' && (
        <>
          <path d="M50 4 L60 22 L40 22 Z" {...p} />
          <circle cx="50" cy="3" r="2" {...p} />
        </>
      )}
      {t === 'R' && (
        <path
          d="M38 24 L38 12 L44 12 L44 18 L48 18 L48 12 L52 12 L52 18 L56 18 L56 12 L62 12 L62 24 Z"
          {...p}
        />
      )}
      {t === 'Q' && (
        <>
          <circle cx="38" cy="14" r="3" {...p} />
          <circle cx="50" cy="8" r="3" {...p} />
          <circle cx="62" cy="14" r="3" {...p} />
          <path d="M36 18 L42 24 L50 12 L58 24 L64 18 L62 26 L38 26 Z" {...p} />
        </>
      )}
      {t === 'K' && (
        <path
          d="M48 0 L52 0 L52 8 L60 8 L60 12 L52 12 L52 20 L48 20 L48 12 L40 12 L40 8 L48 8 Z"
          {...p}
        />
      )}
    </svg>
  );
}

// === 4. PETAL ===============================================================
function petalPath(cx: number, cy: number, angle: number, len: number): string {
  const a = (angle * Math.PI) / 180;
  const x1 = cx + Math.cos(a - 0.5) * len * 0.4;
  const y1 = cy + Math.sin(a - 0.5) * len * 0.4;
  const x2 = cx + Math.cos(a) * len;
  const y2 = cy + Math.sin(a) * len;
  const x3 = cx + Math.cos(a + 0.5) * len * 0.4;
  const y3 = cy + Math.sin(a + 0.5) * len * 0.4;
  return `M ${cx} ${cy} Q ${x1} ${y1} ${x2} ${y2} Q ${x3} ${y3} ${cx} ${cy} Z`;
}

function Petal({ type, isWhite }: { type: PieceType; isWhite: boolean }) {
  const { fill, stroke } = paletteFor(isWhite ? 'white' : 'black');
  const p = { fill, stroke, strokeWidth: 2.8, strokeLinejoin: 'round' as const, strokeLinecap: 'round' as const };
  const t = letterFor(type);
  return (
    <svg viewBox="0 0 100 100" width="100%" height="100%" style={{ display: 'block' }}>
      {t === 'P' && <circle cx="50" cy="50" r="22" {...p} />}
      {t === 'N' && (
        <>
          <path d="M30 30 Q70 28 68 52 Q64 80 36 75 Q22 50 30 30 Z" {...p} />
          <circle cx="55" cy="42" r="2" fill={stroke} stroke="none" />
        </>
      )}
      {t === 'B' && (
        <>
          <path d="M50 12 Q72 38 60 70 Q50 84 40 70 Q28 38 50 12 Z" {...p} />
          <circle cx="50" cy="40" r="3" fill={stroke} stroke="none" />
        </>
      )}
      {t === 'R' && (
        <>
          <circle cx="50" cy="22" r="14" {...p} />
          <circle cx="50" cy="78" r="14" {...p} />
          <circle cx="22" cy="50" r="14" {...p} />
          <circle cx="78" cy="50" r="14" {...p} />
          <circle cx="50" cy="50" r="10" {...p} />
        </>
      )}
      {t === 'Q' && (
        <>
          {[90, 162, 234, 306, 18].map((a, i) => (
            <path key={i} d={petalPath(50, 50, a, 32)} {...p} />
          ))}
          <circle cx="50" cy="50" r="8" fill={stroke} stroke="none" />
        </>
      )}
      {t === 'K' && (
        <>
          {[0, 60, 120, 180, 240, 300].map((a, i) => (
            <path key={i} d={petalPath(50, 50, a, 32)} {...p} />
          ))}
          <circle cx="50" cy="50" r="10" {...p} />
          <circle cx="50" cy="50" r="3" fill={stroke} stroke="none" />
        </>
      )}
    </svg>
  );
}

// === 5. BOTANICAL ===========================================================
function Botanical({ type, isWhite }: { type: PieceType; isWhite: boolean }) {
  const { fill, stroke } = paletteFor(isWhite ? 'white' : 'black');
  const p = { fill, stroke, strokeWidth: 2.4, strokeLinejoin: 'round' as const, strokeLinecap: 'round' as const };
  const vein = { stroke, strokeWidth: 1.8, fill: 'none', strokeLinecap: 'round' as const };
  const t = letterFor(type);
  return (
    <svg viewBox="0 0 100 100" width="100%" height="100%" style={{ display: 'block' }}>
      {t === 'P' && (
        <>
          <path d="M50 38 Q32 50 36 70 Q44 82 56 78 Q70 70 64 50 Q60 42 50 38 Z" {...p} />
          <line x1="50" y1="38" x2="50" y2="26" {...vein} strokeWidth="2.5" />
          <path d="M50 52 Q46 60 50 70" {...vein} />
        </>
      )}
      {t === 'N' && (
        <>
          <path
            d="M50 14 Q40 18 38 26 Q28 28 30 38 Q24 42 28 50 Q24 58 32 62 Q30 72 40 72 Q44 80 50 78 Q56 80 60 72 Q70 72 68 62 Q76 58 72 50 Q76 42 70 38 Q72 28 62 26 Q60 18 50 14 Z"
            {...p}
          />
          <line x1="50" y1="20" x2="50" y2="76" {...vein} />
        </>
      )}
      {t === 'B' && (
        <>
          <path d="M50 8 Q60 20 58 50 Q62 70 50 88 Q38 70 42 50 Q40 20 50 8 Z" {...p} />
          <line x1="50" y1="14" x2="50" y2="82" {...vein} strokeWidth="2" />
          <path
            d="M50 28 L44 34 M50 38 L56 44 M50 50 L44 56 M50 60 L56 66"
            {...vein}
          />
        </>
      )}
      {t === 'R' && (
        <>
          <rect x="44" y="20" width="12" height="60" {...p} />
          <path d="M44 32 L24 28 L28 36 L44 38 Z" {...p} />
          <path d="M56 32 L76 28 L72 36 L56 38 Z" {...p} />
          <path d="M44 52 L26 48 L30 56 L44 58 Z" {...p} />
          <path d="M56 52 L74 48 L70 56 L56 58 Z" {...p} />
          <path d="M30 84 L38 80 L62 80 L70 84 Z" {...p} />
        </>
      )}
      {t === 'Q' && (
        <>
          <line x1="50" y1="60" x2="50" y2="86" {...vein} strokeWidth="3" />
          <path d="M44 80 L36 82 M56 80 L64 82" {...vein} />
          {[0, 45, 90, 135, 180, 225, 270, 315].map((a, i) => {
            const rad = (a * Math.PI) / 180;
            const x = 50 + Math.cos(rad) * 22;
            const y = 38 + Math.sin(rad) * 22;
            return (
              <ellipse
                key={i}
                cx={x}
                cy={y}
                rx="10"
                ry="6"
                transform={`rotate(${a + 90} ${x} ${y})`}
                {...p}
              />
            );
          })}
          <circle cx="50" cy="38" r="9" {...p} />
          <circle cx="50" cy="38" r="3" fill={stroke} stroke="none" />
        </>
      )}
      {t === 'K' && (
        <>
          <rect x="46" y="68" width="8" height="20" {...p} />
          <path d="M50 8 L32 30 L42 30 L26 50 L40 50 L22 72 L78 72 L60 50 L74 50 L58 30 L68 30 Z" {...p} />
        </>
      )}
    </svg>
  );
}

// === 6. BLOCK ===============================================================
function Block({ type, isWhite }: { type: PieceType; isWhite: boolean }) {
  const { fill, stroke } = paletteFor(isWhite ? 'white' : 'black');
  const text = isWhite ? stroke : 'rgb(var(--c-piece-light))';
  const t = letterFor(type);
  return (
    <svg viewBox="0 0 100 100" width="100%" height="100%" style={{ display: 'block' }}>
      <rect x="10" y="10" width="80" height="80" rx="2" fill={fill} stroke={stroke} strokeWidth="3.5" />
      <text
        x="50"
        y="70"
        textAnchor="middle"
        fontFamily="ui-monospace, 'JetBrains Mono', 'IBM Plex Mono', monospace"
        fontWeight="700"
        fontSize="58"
        fill={text}
      >
        {t}
      </text>
    </svg>
  );
}

// === 7. NEON WIRE ===========================================================
function NeonWire({ type, isWhite }: { type: PieceType; isWhite: boolean }) {
  const stroke = isWhite
    ? 'rgb(var(--c-piece-light))'
    : 'rgb(var(--c-piece-dark))';
  const p = { fill: 'none', stroke, strokeWidth: 2, strokeLinejoin: 'miter' as const, strokeLinecap: 'square' as const };
  const t = letterFor(type);
  return (
    <svg
      viewBox="0 0 100 100"
      width="100%"
      height="100%"
      style={{
        display: 'block',
        filter: `drop-shadow(0 0 2px ${stroke}) drop-shadow(0 0 4px ${stroke}88)`,
      }}
    >
      {t === 'P' && (
        <>
          <polygon points="50,20 62,32 62,50 70,68 30,68 38,50 38,32" {...p} />
          <circle cx="50" cy="30" r="6" {...p} />
          <line x1="30" y1="68" x2="70" y2="68" {...p} />
          <line x1="22" y1="80" x2="78" y2="80" {...p} />
        </>
      )}
      {t === 'N' && (
        <>
          <polygon
            points="30,80 30,50 48,50 48,30 60,18 72,30 72,50 60,58 56,55 52,60 60,80"
            {...p}
          />
          <line x1="22" y1="86" x2="78" y2="86" {...p} />
          <circle cx="62" cy="36" r="1.5" fill={stroke} stroke="none" />
        </>
      )}
      {t === 'B' && (
        <>
          <polygon points="50,10 62,28 62,55 50,68 38,55 38,28" {...p} />
          <line x1="50" y1="20" x2="50" y2="50" {...p} />
          <polygon points="32,76 68,76 72,86 28,86" {...p} />
        </>
      )}
      {t === 'R' && (
        <>
          <polygon
            points="22,30 22,18 32,18 32,24 42,24 42,18 58,18 58,24 68,24 68,18 78,18 78,30 72,30 72,60 78,60 78,72 22,72 22,60 28,60 28,30"
            {...p}
          />
          <line x1="22" y1="86" x2="78" y2="86" {...p} />
        </>
      )}
      {t === 'Q' && (
        <>
          <polygon
            points="22,18 30,52 38,28 50,52 62,28 70,52 78,18 74,64 26,64"
            {...p}
          />
          <circle cx="22" cy="16" r="2" {...p} />
          <circle cx="50" cy="10" r="2" {...p} />
          <circle cx="78" cy="16" r="2" {...p} />
          <polygon points="22,80 26,64 74,64 78,80" {...p} />
        </>
      )}
      {t === 'K' && (
        <>
          <line x1="50" y1="6" x2="50" y2="26" {...p} />
          <line x1="40" y1="14" x2="60" y2="14" {...p} />
          <polygon points="22,40 30,28 50,42 70,28 78,40 78,68 22,68" {...p} />
          <polygon points="22,80 26,68 74,68 78,80" {...p} />
        </>
      )}
    </svg>
  );
}

// === 8. WOODCUT =============================================================
function Woodcut({ type, isWhite }: { type: PieceType; isWhite: boolean }) {
  const { fill, stroke, cutBg } = paletteFor(isWhite ? 'white' : 'black');
  const cutColor = isWhite ? cutBg : 'rgb(var(--c-piece-light))';
  const p = { fill, stroke, strokeWidth: 2.5, strokeLinejoin: 'miter' as const, strokeLinecap: 'butt' as const };
  const cut = { stroke: cutColor, strokeWidth: 1.8, fill: 'none', strokeLinecap: 'round' as const };
  const t = letterFor(type);
  return (
    <svg viewBox="0 0 100 100" width="100%" height="100%" style={{ display: 'block' }}>
      {t === 'P' && (
        <>
          <path
            d="M50 16 L60 22 L60 32 L66 42 L66 54 L70 68 L72 76 L78 88 L22 88 L28 76 L30 68 L34 54 L34 42 L40 32 L40 22 Z"
            {...p}
          />
          <path d="M44 30 Q50 32 56 30" {...cut} />
          <path d="M38 56 Q50 58 62 56" {...cut} />
          <path d="M32 78 L68 78" {...cut} />
        </>
      )}
      {t === 'N' && (
        <>
          <path
            d="M28 88 L30 60 L26 50 L30 38 L42 28 Q44 18 50 18 Q60 18 64 28 Q74 32 76 50 L72 56 L62 50 L57 56 L60 88 Z"
            {...p}
          />
          <line x1="50" y1="32" x2="56" y2="30" {...cut} />
          <line x1="44" y1="44" x2="38" y2="48" {...cut} />
          <line x1="40" y1="76" x2="50" y2="74" {...cut} />
        </>
      )}
      {t === 'B' && (
        <>
          <path d="M50 8 L46 14 L52 14 Z" {...p} />
          <path d="M50 16 L60 32 L62 52 L38 52 L40 32 Z" {...p} />
          <rect x="34" y="52" width="32" height="10" {...p} />
          <path d="M28 88 L34 62 L66 62 L72 88 Z" {...p} />
          <line x1="50" y1="22" x2="50" y2="46" {...cut} />
          <line x1="34" y1="74" x2="66" y2="74" {...cut} />
        </>
      )}
      {t === 'R' && (
        <>
          <path
            d="M22 30 L22 16 L32 16 L32 22 L42 22 L42 16 L58 16 L58 22 L68 22 L68 16 L78 16 L78 30 L72 30 L72 60 L78 60 L78 70 L22 70 L22 60 L28 60 L28 30 Z"
            {...p}
          />
          <path d="M22 88 L26 70 L74 70 L78 88 Z" {...p} />
          <line x1="30" y1="42" x2="70" y2="42" {...cut} />
          <line x1="30" y1="54" x2="70" y2="54" {...cut} />
          <line x1="28" y1="78" x2="72" y2="78" {...cut} />
        </>
      )}
      {t === 'Q' && (
        <>
          <path
            d="M18 18 L22 14 L26 18 L30 50 L40 26 L48 50 L50 22 L52 50 L60 26 L70 50 L74 18 L78 14 L82 18 L76 62 L24 62 Z"
            {...p}
          />
          <path d="M24 72 L26 62 L74 62 L76 72 Z" {...p} />
          <path d="M22 88 L26 72 L74 72 L78 88 Z" {...p} />
          <line x1="30" y1="56" x2="70" y2="56" {...cut} />
          <line x1="26" y1="80" x2="74" y2="80" {...cut} />
        </>
      )}
      {t === 'K' && (
        <>
          <rect x="46" y="4" width="8" height="20" {...p} />
          <rect x="38" y="10" width="24" height="6" {...p} />
          <path d="M22 32 L36 40 L50 28 L64 40 L78 32 L78 58 L22 58 Z" {...p} />
          <path d="M22 70 L26 58 L74 58 L78 70 Z" {...p} />
          <path d="M22 88 L26 70 L74 70 L78 88 Z" {...p} />
          <line x1="32" y1="50" x2="68" y2="50" {...cut} />
          <line x1="28" y1="78" x2="72" y2="78" {...cut} />
        </>
      )}
    </svg>
  );
}

// === Dispatcher =============================================================
const RENDERERS: Record<
  InlinePieceSetId,
  (props: { type: PieceType; isWhite: boolean }) => JSX.Element
> = {
  heraldic: Heraldic,
  geometric: Geometric,
  ornate: Ornate,
  petal: Petal,
  botanical: Botanical,
  block: Block,
  neon_wire: NeonWire,
  woodcut: Woodcut,
};

export function isInlineSet(id: string): id is InlinePieceSetId {
  return id in RENDERERS;
}

export function InlinePiece({
  setId,
  type,
  color,
}: {
  setId: InlinePieceSetId;
  type: PieceType;
  color: Color;
}) {
  const Renderer = RENDERERS[setId];
  return <Renderer type={type} isWhite={color === 'white'} />;
}

export const INLINE_PIECE_SETS: Array<{
  id: InlinePieceSetId;
  name: string;
  blurb: string;
}> = [
  { id: 'heraldic', name: 'Heraldic', blurb: 'Traditional Staunton with hand-cut feel.' },
  { id: 'geometric', name: 'Geometric', blurb: 'Sharp polygons, machined silhouettes.' },
  { id: 'ornate', name: 'Ornate', blurb: 'Slim columns, filigree dots, varied finials.' },
  { id: 'petal', name: 'Petal', blurb: 'Soft organic blossoms, no corners.' },
  { id: 'botanical', name: 'Botanical', blurb: 'Each piece a different plant or tree.' },
  { id: 'block', name: 'Block', blurb: 'Brutalist letter on a hard square.' },
  { id: 'neon_wire', name: 'Neon Wire', blurb: 'Outline only with electric glow.' },
  { id: 'woodcut', name: 'Woodcut', blurb: 'Stamp silhouettes with cut detail.' },
];
